import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import TenantItem from '@/component/Auth/TenantItem';
import { useUserRoleMapper } from '@/hook';
import {
  AccountCircle,
  AdminPanelSettings,
  Done,
  VBasePopover,
  VFlex,
  VIconButton,
  VStack,
  new__cn as cn,
} from '@/package';
import { useAuthStore, useDelegationStore, useTenantStore } from '@/store';

const UserPopover = ({
  setIsMyAccountVIsible,
}: {
  setIsMyAccountVIsible: Dispatch<SetStateAction<boolean>>;
}) => {
  const [visible, setVisible] = useState(false);
  const { isDelegationMode } = useTenantStore();

  const close = () => setVisible(false);

  return (
    <VBasePopover
      placement="bottom-right"
      content={() => (
        <UserContent
          close={close}
          setIsMyAccountVIsible={setIsMyAccountVIsible}
        />
      )}
      className="rounded-md! shadow-sm!"
      trigger="click"
      visible={visible}
      onUpdateVisible={setVisible}
    >
      <VIconButton
        size="lg"
        variant="roundSecondary"
        Icon={AccountCircle}
        className={cn({
          'text-warning-500': isDelegationMode(),
        })}
      />
    </VBasePopover>
  );
};

const UserContent = ({
  close,
  setIsMyAccountVIsible,
}: {
  close: () => void;
  setIsMyAccountVIsible: Dispatch<SetStateAction<boolean>>;
}) => {
  const { user, signOut } = useAuthStore();
  const { getCurrentTenant } = useTenantStore();
  const currentTenant = getCurrentTenant();
  const navigate = useNavigate();
  const { tenantList, selectTenant } = useTenantStore();
  const { mapUserRole } = useUserRoleMapper();
  const isAdmin = user.role === 'Admin' || user.role === 'SiteAdmin';
  const { isDelegationMode } = useTenantStore();
  const { clear } = useDelegationStore();

  return (
    <VStack className="w-[248px] pt-4 pb-2" gap={0.5}>
      <VStack className="w-full" items="center" gap={1}>
        <VStack className="w-full" items="center" justify="center" gap={0.25}>
          {isDelegationMode() ? (
            <>
              <AdminPanelSettings className="size-20 text-blue-300" />
              <VFlex
                className="h-[30px] w-[132px] rounded-sm bg-skyBlue-200 font-semibold text-blue-300"
                justify="center"
                items="center"
              >
                read-only mode
              </VFlex>
            </>
          ) : (
            <AccountCircle className="size-20 text-primary-500" />
          )}
          <VStack className="w-full" items="center">
            <VFlex items="center" className="text-primary-500" gap={0.5}>
              <div className="text-body-lg font-semibold">
                {user.name || user.email.split('@').at(0)}
              </div>

              <div className="h-4 w-px bg-gray-300" />

              <div className="text-body-lg">{mapUserRole(user.role)}</div>
            </VFlex>
            <div className="text-body-lg font-semibold text-primary-500">
              {user.email}
            </div>
          </VStack>
        </VStack>

        <VStack className="w-full px-2" gap={0.5}>
          <VFlex gap={0.5}>
            <div className="w-14 text-body-md text-gray-400">Company</div>
            <div className="text-body-md font-semibold text-primary-500">
              {currentTenant?.tenantName || ''}
            </div>
          </VFlex>

          {isAdmin && (
            <VFlex gap={0.5}>
              <div className="w-14 text-body-md text-gray-400">Plan</div>
              <div className="text-body-md font-semibold text-primary-500">
                {currentTenant?.tier
                  ? currentTenant.tier === 'standard'
                    ? 'Enterprise'
                    : currentTenant.tier
                  : ''}
              </div>
            </VFlex>
          )}
        </VStack>
      </VStack>

      <VStack className="px-2">
        <div className="text-body-md text-gray-400">Switch workspace</div>
        <VStack gap={0.25}>
          <VStack className="max-h-[180px] overflow-y-auto p-0.5">
            {tenantList.map(t => {
              const isSelected = currentTenant?.tenantID === t.tenantID;
              const isDelegation = t.delegation;

              return (
                <TenantItem
                  key={t.tenantID}
                  {...t}
                  Icon={Done}
                  iconClassName={cn('h-6 w-6', {
                    'text-blue-300': isSelected,
                    'hidden ': !isSelected,
                  })}
                  className="border-none"
                  onClick={() => {
                    if (!isDelegation) clear();

                    selectTenant(t.tenantID);
                    close();
                  }}
                />
              );
            })}
          </VStack>
        </VStack>
      </VStack>

      <VStack className="divide-y divide-gray-200 border-t border-b border-gray-200">
        <div
          className={cn(
            'p-2 text-body-lg',
            'hover:relative',
            'hover:after:absolute hover:after:top-0 hover:after:left-0',
            'hover:after:h-full hover:after:w-full',
            'hover:after:rounded-md',
            'hover:after:content-[""]',
            'hover:after:shadow-sm',
            'hover:after:z-[1]',
          )}
          role="button"
          onClick={() => {
            navigate('/member');
            close();
          }}
        >
          Members
        </div>

        <div
          className={cn(
            'p-2 text-body-lg',
            'hover:relative',
            'hover:after:absolute hover:after:top-0 hover:after:left-0',
            'hover:after:h-full hover:after:w-full',
            'hover:after:rounded-md',
            'hover:after:content-[""]',
            'hover:after:shadow-sm',
            'hover:after:z-[1]',
          )}
          role="button"
          onClick={() => {
            setIsMyAccountVIsible(true);
            close();
          }}
        >
          My account
        </div>

        {isAdmin && (
          <div
            className={cn(
              'p-2 text-body-lg',
              'hover:relative',
              'hover:after:absolute hover:after:top-0 hover:after:left-0',
              'hover:after:h-full hover:after:w-full',
              'hover:after:rounded-md',
              'hover:after:content-[""]',
              'hover:after:shadow-sm',
              'hover:after:z-[1]',
            )}
            role="button"
            onClick={() => {
              navigate('/admin-setting');
              close();
            }}
          >
            Admin settings
          </div>
        )}

        {isDelegationMode() ? (
          <a
            className={cn(
              'p-2 text-body-lg text-blue-300',
              'hover:relative',
              'hover:after:absolute hover:after:top-0 hover:after:left-0',
              'hover:after:h-full hover:after:w-full',
              'hover:after:rounded-md',
              'hover:after:content-[""]',
              'hover:after:shadow-sm',
              'hover:after:z-[1]',
            )}
            target="_blank"
            href={`${import.meta.env.DEV ? import.meta.env.VITE_APP_DEV_ADMIN_URL : import.meta.env.VITE_APP_ADMIN_URL}`}
          >
            Return to VueX admin
          </a>
        ) : (
          <div
            className={cn(
              'p-2 text-body-lg text-error-500',
              'hover:relative',
              'hover:after:absolute hover:after:top-0 hover:after:left-0',
              'hover:after:h-full hover:after:w-full',
              'hover:after:rounded-md',
              'hover:after:content-[""]',
              'hover:after:shadow-sm',
              'hover:after:z-[1]',
            )}
            role="button"
            onClick={() => {
              signOut();
              close();
            }}
          >
            Log out
          </div>
        )}
      </VStack>
    </VStack>
  );
};

export default UserPopover;
